import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { Percent, Activity, HeartPulse } from 'lucide-react';

/** Presentes · Ausentes · Sem chamada */
const PIE_COLORS = ['#10b981', '#ef4444', '#94a3b8'];

export type AnalyticsChartsMode = 'dia' | 'turno';

interface Props {
  mode: AnalyticsChartsMode;
  scopeLabel: string;
  presentes: number;
  ausentes: number;
  semRegistro: number;
  pctPresenca: number | null;
  alunosBase: number;
  /** Comparativo compacto: % no outro escopo */
  pctComparativo?: number | null;
  labelComparativo?: string;
  /** Extra só no modo dia */
  turnoChartData?: { turno: string; matriculados: number }[];
  riscoChartData?: { turma: string; emRisco: number }[];
  totalEmRisco?: number;
  turmasTurnoChartData?: { turma: string; alunos: number }[];
  /** Se true, só frequência + barra (sem 2º/3º card) */
  compact?: boolean;
  isLoading?: boolean;
}

const tooltipStyle = {
  fontSize: 10,
  fontWeight: 500,
  borderRadius: 6,
  border: '1px solid #e8ecf1',
  boxShadow: '0 2px 8px rgba(15, 23, 42, 0.06)',
};

export function AdminDesktopAnalyticsCharts({
  mode,
  scopeLabel,
  presentes,
  ausentes,
  semRegistro,
  pctPresenca,
  alunosBase,
  pctComparativo = null,
  labelComparativo,
  turnoChartData = [],
  riscoChartData = [],
  totalEmRisco = 0,
  turmasTurnoChartData = [],
  compact = false,
  isLoading,
}: Props) {
  const hasRegistros = presentes + ausentes > 0;
  const pct = pctPresenca ?? (alunosBase > 0 ? Math.round((presentes / alunosBase) * 100) : 0);
  const presencaPie = hasRegistros
    ? [
        { name: 'Presentes', value: presentes },
        { name: 'Ausentes', value: ausentes },
        ...(semRegistro > 0 ? [{ name: 'Sem registro', value: semRegistro }] : []),
      ]
    : [{ name: 'Sem dados', value: 1 }];

  const showExtras = !compact;
  const skelCount = compact ? 1 : mode === 'dia' ? 3 : 2;

  if (isLoading) {
    return (
      <div
        className={`adm-desk-charts adm-desk-charts--loading${compact ? ' adm-desk-charts--compact' : mode === 'turno' ? ' adm-desk-charts--turno' : ''}`}
        aria-busy="true"
      >
        {Array.from({ length: skelCount }, (_, i) => (
          <div key={i} className="adm-desk-chart-skel" />
        ))}
      </div>
    );
  }

  return (
    <div className="adm-desk-charts-wrap">
      {pctComparativo != null && labelComparativo ? (
        <p className="adm-desk-compare-line" role="note">
          <strong>{mode === 'turno' ? `Turno ${scopeLabel}` : 'No dia'}:</strong>{' '}
          {hasRegistros ? `${pct}%` : '—'}
          <span aria-hidden> · </span>
          <span>
            {labelComparativo}: <strong>{pctComparativo}%</strong>
          </span>
        </p>
      ) : null}

      <section
        className={`adm-desk-charts${
          compact
            ? ' adm-desk-charts--compact'
            : mode === 'turno'
              ? ' adm-desk-charts--turno'
              : ' adm-desk-charts--dia'
        }`}
        aria-label={mode === 'dia' ? 'Frequência no dia' : `Frequência · ${scopeLabel}`}
      >
        <div className="adm-desk-chart-card adm-desk-chart-card--freq">
          <div className="adm-desk-chart-head">
            <Percent size={13} strokeWidth={1.75} />
            <span>{mode === 'dia' ? 'Frequência no dia' : `Frequência · ${scopeLabel}`}</span>
            <span className="adm-desk-chart-scope">{mode === 'dia' ? 'escola' : 'turno'}</span>
          </div>
          <div className="adm-desk-freq-row">
            <div className="adm-desk-freq-hero">
              <span className="adm-desk-freq-pct">{hasRegistros ? `${pct}%` : '—'}</span>
              <div className="adm-desk-donut-sm" aria-hidden>
                <PieChart width={72} height={72}>
                  <Pie
                    data={presencaPie}
                    dataKey="value"
                    cx="50%"
                    cy="50%"
                    innerRadius={22}
                    outerRadius={32}
                    paddingAngle={hasRegistros ? 2 : 0}
                    stroke="none"
                    startAngle={90}
                    endAngle={-270}
                    isAnimationActive={false}
                  >
                    {presencaPie.map((_, i) => (
                      <Cell
                        key={i}
                        fill={hasRegistros ? PIE_COLORS[i % PIE_COLORS.length] : PIE_COLORS[2]}
                      />
                    ))}
                  </Pie>
                </PieChart>
              </div>
            </div>

            <div className="adm-desk-freq-tiles" aria-label="Detalhe da frequência">
              <div className="adm-desk-freq-tile" title="Alunos no escopo">
                <strong>{alunosBase}</strong>
                <span>Alunos</span>
              </div>
              <div
                className="adm-desk-freq-tile adm-desk-freq-tile--pres"
                title="Presentes na chamada"
              >
                <strong>{presentes}</strong>
                <span>Presentes</span>
              </div>
              <div className="adm-desk-freq-tile adm-desk-freq-tile--aus" title="Ausentes">
                <strong>{ausentes}</strong>
                <span>Ausentes</span>
              </div>
              <div
                className="adm-desk-freq-tile adm-desk-freq-tile--sem"
                title="Sem chamada lançada"
              >
                <strong>{semRegistro}</strong>
                <span>S/ chamada</span>
              </div>
            </div>
          </div>
        </div>

        {showExtras && mode === 'dia' ? (
          <>
            <div className="adm-desk-chart-card adm-desk-chart-card--line">
              <div className="adm-desk-chart-head">
                <Activity size={13} strokeWidth={1.75} />
                <span>Matrículas por turno</span>
                <span className="adm-desk-chart-scope">escola</span>
              </div>
              <div className="adm-desk-line-chart">
                {turnoChartData.length === 0 ? (
                  <p className="adm-desk-chart-empty">Sem dados de turno.</p>
                ) : (
                  <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={120}>
                    <LineChart data={turnoChartData} margin={{ top: 4, right: 6, left: -22, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                      <XAxis
                        dataKey="turno"
                        tick={{ fontSize: 9, fill: '#94a3b8', fontWeight: 500 }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        allowDecimals={false}
                        tick={{ fontSize: 8, fill: '#cbd5e1', fontWeight: 500 }}
                        width={20}
                        axisLine={false}
                        tickLine={false}
                      />
                      <Tooltip contentStyle={tooltipStyle} formatter={(v: any) => [`${v}`, 'Alunos']} />
                      <Line
                        type="monotone"
                        dataKey="matriculados"
                        stroke="#6366f1"
                        strokeWidth={2}
                        dot={{ r: 3, fill: '#6366f1', stroke: '#fff', strokeWidth: 1.5 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            <div className="adm-desk-chart-card adm-desk-chart-card--risco">
              <div className="adm-desk-chart-head">
                <HeartPulse size={13} strokeWidth={1.75} />
                <span>Risco de evasão</span>
                <span className="adm-desk-chart-scope">mês · escola</span>
              </div>
              <div className="adm-desk-risco-summary">
                <span className="adm-desk-risco-total">{totalEmRisco}</span>
                <span className="adm-desk-risco-meta">alunos {'<'}75% freq. (mês)</span>
              </div>
              <div className="adm-desk-line-chart adm-desk-line-chart--risco">
                {riscoChartData.length === 0 ? (
                  <p className="adm-desk-chart-empty">Sem indicadores de risco.</p>
                ) : (
                  <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={120}>
                    <LineChart data={riscoChartData} margin={{ top: 4, right: 6, left: -22, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#fef2f2" vertical={false} />
                      <XAxis
                        dataKey="turma"
                        tick={{ fontSize: 9, fill: '#94a3b8', fontWeight: 500 }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        allowDecimals={false}
                        tick={{ fontSize: 8, fill: '#fecaca', fontWeight: 500 }}
                        width={20}
                        axisLine={false}
                        tickLine={false}
                      />
                      <Tooltip contentStyle={tooltipStyle} formatter={(v: any) => [`${v}`, 'Em risco']} />
                      <Line
                        type="monotone"
                        dataKey="emRisco"
                        stroke="#ef4444"
                        strokeWidth={2}
                        dot={{ r: 3, fill: '#ef4444', stroke: '#fff', strokeWidth: 1.5 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </>
        ) : null}

        {showExtras && mode === 'turno' ? (
          <div className="adm-desk-chart-card">
            <div className="adm-desk-chart-head">
              <Activity size={13} strokeWidth={1.75} />
              <span>Alunos por turma · {scopeLabel}</span>
              <span className="adm-desk-chart-scope">turno</span>
            </div>
            <div className="adm-desk-line-chart">
              {turmasTurnoChartData.length === 0 ? (
                <p className="adm-desk-chart-empty">Sem turmas neste turno.</p>
              ) : (
                <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={120}>
                  <LineChart
                    data={turmasTurnoChartData}
                    margin={{ top: 4, right: 6, left: -22, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis
                      dataKey="turma"
                      tick={{ fontSize: 9, fill: '#94a3b8', fontWeight: 500 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      allowDecimals={false}
                      tick={{ fontSize: 8, fill: '#cbd5e1', fontWeight: 500 }}
                      width={20}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip contentStyle={tooltipStyle} formatter={(v: any) => [`${v}`, 'Alunos']} />
                    <Line
                      type="monotone"
                      dataKey="alunos"
                      stroke="#2563eb"
                      strokeWidth={2}
                      dot={{ r: 3, fill: '#2563eb', stroke: '#fff', strokeWidth: 1.5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        ) : null}
      </section>
    </div>
  );
}
